import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { tint } from '@frontend/colors';
import { useDevToolsStore } from '@frontend/devtools';
import { theme } from '@frontend/theme';
import { AvailabilityExceptionInfo, CalendarStripeLineInfo, WorkingHourInfo } from './types';
import { PX_PER_MINUTE, zIndex } from './utils';

const TIME_FORMAT = 'hh:mm A';
const MAX_DAY_MINUTES = 24 * 60;

enum IndicatorType {
  NonWorkingHours = 'non-working-hours', // this is the full day stripe background
  WorkingHours = 'working-hours', // this is the working hours gray background
  BreakHours = 'break-hours', // this is the break hours stripe background
  AvailabilityException = 'availability-exception', // this is the availability exception hours gray background
}

type IndicatorColorInfo = {
  background: string;
  stripe?: string;
};

const COLORS: Record<IndicatorType, IndicatorColorInfo> = {
  [IndicatorType.NonWorkingHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [IndicatorType.WorkingHours]: {
    background: theme.colors.neutral5,
  },
  [IndicatorType.BreakHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [IndicatorType.AvailabilityException]: {
    background: theme.colors.neutral5,
  },
};

const DEBUG_COLORS: Record<IndicatorType, IndicatorColorInfo> = {
  [IndicatorType.NonWorkingHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [IndicatorType.WorkingHours]: {
    background: theme.colors.neutral5,
  },
  [IndicatorType.BreakHours]: {
    background: theme.colors.critical5,
    stripe: theme.colors.critical20,
  },
  [IndicatorType.AvailabilityException]: {
    background: theme.colors.success10,
  },
};

const getStartMinutes = (startTime?: string): number =>
  startTime ? dayjs(startTime, TIME_FORMAT).diff(dayjs().startOf('day'), 'minute') : 0;

interface WorkingHoursIndicatorProps {
  workingHours?: WorkingHourInfo[];
  availabilityExceptions?: AvailabilityExceptionInfo[];
  isProvider?: boolean;
  hasAnyWorkingHoursSet?: boolean;
}

export const WorkingHoursIndicator = ({
  workingHours,
  availabilityExceptions,
  isProvider = false,
  hasAnyWorkingHoursSet = true,
}: WorkingHoursIndicatorProps) => {
  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  // there should be only one working hour for selected date
  const workingHour = workingHours?.[0];

  const defaultProps = { debug: isDebugModeOn, isProvider };

  return (
    <div css={{ width: '100%', height: '100%', position: 'absolute', zIndex: zIndex.workingHoursContainer }}>
      {hasAnyWorkingHoursSet && (
        <>
          <IndicatorItem
            startTime='00:00 AM'
            duration={MAX_DAY_MINUTES}
            type={IndicatorType.NonWorkingHours}
            {...defaultProps}
          />
          <IndicatorItem
            startTime={workingHour?.startTime ?? ''}
            duration={workingHour?.duration ?? 0}
            type={IndicatorType.WorkingHours}
            {...defaultProps}
          />
          {workingHour?.breaks?.map(({ startTime, duration }) => (
            <IndicatorItem
              key={`break-hours-${startTime}-${duration}`}
              startTime={startTime}
              duration={duration}
              type={IndicatorType.BreakHours}
              {...defaultProps}
            />
          ))}
        </>
      )}
      {availabilityExceptions?.map(({ startTime, duration }) => (
        <IndicatorItem
          key={`availability-exception-${startTime}-${duration}`}
          startTime={startTime}
          duration={duration}
          type={IndicatorType.AvailabilityException}
          {...defaultProps}
        />
      ))}
    </div>
  );
};

interface IndicatorItemProps extends CalendarStripeLineInfo {
  type: IndicatorType;
  isProvider: boolean;
  debug: boolean;
}

const IndicatorItem = ({ startTime, duration = 0, type, debug, isProvider }: IndicatorItemProps) => {
  if (!startTime || duration <= 0) {
    return null;
  }

  const startMinutes = getStartMinutes(startTime);
  const { background, stripe } = debug ? DEBUG_COLORS[type] : COLORS[type];
  return (
    <div
      data-debug={debug ? `${type}-${startTime}-${duration}` : undefined}
      css={[hoursRectangleStyle(startMinutes, duration), stripeBackground(background, stripe, isProvider)]}
    />
  );
};

const hoursRectangleStyle = (startMinutes = 0, duration = 0) => {
  // safeguard for negative start minutes
  if (startMinutes + duration > MAX_DAY_MINUTES) {
    duration = MAX_DAY_MINUTES - startMinutes;
  }

  return css({
    position: 'absolute',
    top: startMinutes * PX_PER_MINUTE,
    height: duration * PX_PER_MINUTE,
    width: `calc(100% + ${theme.spacing(2)})`,
    marginLeft: theme.spacing(-1),
  });
};

const stripeBackground = (bgColor: string, stripeColor?: string, isProvider?: boolean) => {
  if (!stripeColor || stripeColor === bgColor) {
    return css({
      background: bgColor,
    });
  }

  return css({
    background: `repeating-linear-gradient(135deg, ${bgColor} 0 0.6rem, ${tint(
      isProvider ? 0.25 : 0.5,
      stripeColor
    )} 0.6rem 0.9rem)`,
    backgroundPosition: '0 0',
    backgroundRepeat: 'repeat',
    backgroundAttachment: 'fixed',
  });
};
